import { alertRuleEngineBaseline } from "@/showcases/alert-rule-engine-versions";
import type { ShowcaseDefinition } from "@/showcases/types";

const product = { color: "green", name: "Theo", role: "Product" } as const;
const platform = { color: "orange", name: "Lina", role: "Platform" } as const;
const systems = { color: "blue", name: "Owen", role: "Systems" } as const;
const reliability = { color: "pink", name: "Priya", role: "Reliability" } as const;

function replaceRequired(html: string, target: string, replacement: string) {
  if (!html.includes(target)) throw new Error(`Alert showcase marker missing: ${target}`);
  return html.replace(target, replacement);
}

function addStyles(html: string, styles: string) {
  return replaceRequired(html, "</style>", `${styles}</style>`);
}

function addBeforeBodyEnd(html: string, markup: string) {
  return replaceRequired(html, "</body>", `${markup}</body>`);
}

const intrinsicBaseline = replaceRequired(
  alertRuleEngineBaseline,
  ".app{min-height:100vh;",
  ".app{min-height:0;",
);

const compactBaseline = addStyles(
  intrinsicBaseline,
  `
  .app{min-height:0;padding:18px 22px}
  .top{margin-bottom:12px}.top h1{font-size:36px}.top .sub{font-size:12px}
  .stepbar{margin-bottom:12px;padding:9px 0}
  .work{gap:18px}.diagram{min-height:0;padding:16px 22px}
  .node{min-height:66px;padding:9px 12px}.node h2{margin:4px 0 2px}.node p{line-height:1.3}.code{margin-top:5px}
  .queue-node{min-height:60px}.eval-shell{padding:10px}.eval-node{min-height:76px}
  .merge,.branch{height:28px}.merge .stem,.branch .left,.branch .right{height:12px}.merge .stem{top:12px}.branch .stem{height:12px}.branch .rail-left,.branch .rail-right{top:11px}.branch .left,.branch .right{top:11px}.vconnector{height:18px}
  .region-head{margin-bottom:7px}.inspect{padding-top:2px}.inspect h2{margin:10px 0 5px;font-size:18px}.copy{min-height:0;margin-bottom:10px}.section{padding:10px 0}.rule{padding:9px}.state{gap:6px 12px}.result{padding-top:11px}.hint{margin-top:8px}
  `,
);

const roughDraft = addStyles(
  replaceRequired(
    compactBaseline,
    "One queue, one evaluator, two ways in and two ways out.",
    "Working draft · map the trigger, evaluation, and delivery boundaries.",
  ),
  `
  .inspect .section,.inspect .result{display:none}
  #readNode,#conditionNode,#decisionNode,.hconnector{opacity:.16}
  .eval-shell:after{display:block;margin-top:10px;color:#667080;font-size:10px;content:"Evaluator stages still need clarification"}
  `,
);

const decisionRevision = addStyles(
  compactBaseline,
  `
  #decisionNode{overflow:visible}
  #decisionNode:after{position:absolute;right:8px;bottom:-18px;border:1px solid #45526a;border-radius:999px;padding:4px 7px;background:#131927;color:#9dd7ff;font-size:8px;content:"eligible? · dedupe clear?"}
  `,
);

const engineeringPanel = `<section class="engineering-review" data-artifact-id="engineering-review"><div><strong>Lina · Platform</strong><span>Queue</span><p>One rule + item key owns retries and deduplication.</p></div><div><strong>Owen · Systems</strong><span>State</span><p>Read persisted item state before evaluating any condition.</p></div><div><strong>Priya · Reliability</strong><span>Outcomes</span><p>Deferred and immediate paths are both observable results.</p></div></section>`;

const finalReview = addBeforeBodyEnd(
  addStyles(
    decisionRevision,
    `
    .engineering-review{max-width:1380px;margin:18px auto 0;display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
    .engineering-review>div{border-top:2px solid #8b7cff;border-radius:8px;padding:12px;background:#10141a}
    .engineering-review strong{display:block;font-size:12px}.engineering-review span{display:block;margin:3px 0 7px;color:#8b7cff;font-size:9px;font-weight:800;text-transform:uppercase}.engineering-review p{margin:0;color:#8893a2;font-size:10px;line-height:1.4}
    `,
  ),
  engineeringPanel,
);

const versions = [roughDraft, compactBaseline, decisionRevision, finalReview];

export const onboardingFlow: ShowcaseDefinition = {
  category: "Systems · Product · Engineering",
  comments: [
    {
      author: systems,
      replies: [
        {
          author: platform,
          text: "Agreed. I’ll split it into Load rule, Read item, Evaluate, and Decide so each retry boundary is visible.",
        },
        {
          author: product,
          text: "Keep the item-state inspector beside the diagram; it explains what changes while the topology stays fixed.",
        },
      ],
      targetId: "rule.evaluator",
      text: "The evaluator is still a black box. Clarify its internal stages and show the current item state beside the flow.",
    },
    {
      author: reliability,
      replies: [
        {
          author: systems,
          text: "The Decision stage should test eligibility and dedupe state before selecting a branch.",
        },
        {
          author: platform,
          text: "I’ll surface those checks directly on the decision point without adding another stage.",
        },
      ],
      targetId: "decision.route",
      text: "Clarify what Decide actually decides. The branch should depend on eligibility and whether delivery was already recorded.",
    },
    {
      author: platform,
      replies: [
        {
          author: reliability,
          text: "Yes. Deferred is a successful result, and Immediate must remain idempotent across retries.",
        },
        {
          author: systems,
          text: "Let’s capture those ownership decisions in a final engineering review below the walkthrough.",
        },
      ],
      targetId: "outcome.fire-at",
      text: "Add the engineering conclusions: who owns queue dedupe, source-of-truth state, and the two outcome contracts?",
    },
  ],
  description: "An interactive split-flow alert engine evolves through a focused engineering review.",
  openComments: [
    {
      author: reliability,
      replies: [
        {
          author: platform,
          text: "The final review now names the owner and invariant for the queue, state read, and outcomes.",
        },
      ],
      targetId: "engineering-review",
      text: "This is the right shape. Before shipping, verify every stage has one owner, one observable transition, and one retry rule.",
    },
    {
      author: systems,
      replies: [
        {
          author: product,
          text: "Both scenarios now step through the same shared middle while activating different input and outcome branches.",
        },
      ],
      targetId: "alert-engine.walkthrough",
      text: "Keep the interaction faithful to the architecture: separate inputs, shared evaluator, split outcomes, and item state on the side.",
    },
  ],
  slug: "alert-rule-engine",
  title: "Design an alert rule engine",
  versions,
};
